"use client";

import React, { useEffect, useState } from "react";
import type { Product } from "@/src/types/tenant";

interface DealOfTheDayProps {
  product?: Product;
}

function getEndOfDayCountdown() {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(23, 59, 59, 999);
  const diff = midnight.getTime() - now.getTime();
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1_000);
  return { h, m, s };
}

export const DealOfTheDay: React.FC<DealOfTheDayProps> = ({ product }) => {
  const [time, setTime] = useState(getEndOfDayCountdown);

  useEffect(() => {
    const t = setInterval(() => setTime(getEndOfDayCountdown()), 1000);
    return () => clearInterval(t);
  }, []);

  const name = product?.name ?? "Featured Deal";
  const price = product?.pricing?.offerPrice ?? product?.pricing?.sellingPrice ?? 999;
  const originalPrice = product?.pricing?.mrp ?? product?.pricing?.sellingPrice ?? 1500;
  const discount = originalPrice > price ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;
  const savings = originalPrice - price;
  const desc =
    product?.description ??
    "A featured local favourite, available today at a special GrabLocal price. Handpicked by our curators for quality and value.";
  const image = product?.images?.thumbnail;

  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <section className="mt-[clamp(40px,5vw,64px)]">
      <div
        className="relative overflow-hidden rounded-[clamp(16px,2vw,22px)] p-[clamp(24px,3.5vw,40px)] md:grid md:grid-cols-[1.2fr_1fr] md:items-center"
        style={{
          background: "linear-gradient(105deg, var(--techno-black) 0%, #2D2F35 55%, #3A2D24 100%)",
          color: "#fff",
          gap: "clamp(24px,4vw,48px)",
        }}
      >
        {/* Noise texture */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            opacity: 0.08,
          }}
        />
        {/* Radial glow */}
        <div
          className="pointer-events-none absolute"
          style={{
            top: -120,
            right: -120,
            width: 320,
            height: 320,
            borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(255,193,59,0.2), transparent 70%)",
          }}
        />

        {/* Left: info */}
        <div className="relative z-10 min-w-0">
          {/* Tag */}
          <span
            className="mb-[18px] inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[1.4px]"
            style={{ background: "var(--turmeric)", color: "var(--techno-black)" }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2s4 4 4 8a4 4 0 11-8 0c0-2 1-3 1-3s-3 2-3 6a6 6 0 1012 0c0-6-6-11-6-11z" />
            </svg>
            Deal of the day
          </span>

          {/* Title */}
          <h2
            className="mb-3.5 leading-tight"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(28px,4.5vw,48px)",
              fontWeight: 400,
              letterSpacing: "-1px",
            }}
          >
            {name}{" "}
            {discount > 0 && (
              <em style={{ fontStyle: "italic", color: "var(--turmeric)" }}>
                — save ₹{savings}.
              </em>
            )}
          </h2>

          {/* Desc */}
          <p
            className="mb-6 max-w-[440px]"
            style={{
              fontSize: "clamp(13.5px,1.7vw,15px)",
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.75)",
            }}
          >
            {desc}
          </p>

          {/* Price row */}
          <div className="mb-5 flex flex-wrap items-baseline gap-3">
            <span
              className="font-bold"
              style={{ fontSize: "clamp(28px,4vw,36px)", letterSpacing: "-0.5px" }}
            >
              ₹{price}
            </span>
            {originalPrice > price && (
              <span
                className="font-medium"
                style={{ fontSize: 18, textDecoration: "line-through", color: "rgba(255,255,255,0.45)" }}
              >
                ₹{originalPrice}
              </span>
            )}
            {discount > 0 && (
              <span
                className="rounded-[6px] px-2.5 py-1 text-xs font-bold"
                style={{ background: "var(--turmeric)", color: "var(--techno-black)" }}
              >
                Save {discount}%
              </span>
            )}
          </div>

          {/* Countdown timer */}
          <div className="mb-7 flex flex-wrap items-center gap-3">
            <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.6)" }}>
              Ends in
            </span>
            <div className="flex items-center gap-1">
              <TimerBlock value={pad(time.h)} unit="Hrs" />
              <span className="px-0.5 text-lg font-bold" style={{ color: "rgba(255,255,255,0.3)" }}>:</span>
              <TimerBlock value={pad(time.m)} unit="Min" />
              <span className="px-0.5 text-lg font-bold" style={{ color: "rgba(255,255,255,0.3)" }}>:</span>
              <TimerBlock value={pad(time.s)} unit="Sec" />
            </div>
          </div>

          {/* CTA */}
          <button
            className="inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 font-bold text-sm transition"
            style={{ background: "var(--turmeric)", color: "var(--techno-black)" }}
            onMouseEnter={e => (e.currentTarget.style.background = "#FFD062")}
            onMouseLeave={e => (e.currentTarget.style.background = "var(--turmeric)")}
          >
            Grab this deal
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Right: visual */}
        <div className="relative z-10 flex items-center justify-center">
          <div
            className="relative flex items-center justify-center rounded-[20px]"
            style={{
              width: "clamp(180px,24vw,260px)",
              height: "clamp(180px,24vw,260px)",
              background: "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            {/* Discount badge */}
            {discount > 0 && (
              <div
                className="absolute -right-4 -top-4 flex flex-col items-center justify-center rounded-full font-extrabold text-white"
                style={{
                  width: 76,
                  height: 76,
                  background: "var(--rising-sun)",
                  transform: "rotate(-12deg)",
                  boxShadow: "0 8px 24px rgba(228,96,78,0.4)",
                  gap: 2,
                }}
              >
                <span style={{ fontSize: 22, lineHeight: 1 }}>{discount}%</span>
                <span style={{ fontSize: 10, letterSpacing: 1 }}>OFF</span>
              </div>
            )}

            {/* Product image or placeholder */}
            {image?.startsWith("http") ? (
              <img src={image} alt={name} className="h-full w-full rounded-[18px] object-cover" />
            ) : (
              <svg viewBox="0 0 72 72" fill="none" style={{ width: "70%", height: "70%" }}>
                <ellipse cx="36" cy="58" rx="22" ry="4" fill="#F0D0C8" opacity="0.4" />
                <rect x="14" y="30" rx="4" width="44" height="28" fill="#D94030" stroke="#B83028" strokeWidth="1.5" />
                <rect x="14" y="24" rx="4" width="44" height="10" fill="#E85040" stroke="#B83028" strokeWidth="1.5" />
                <rect x="33" y="24" width="6" height="34" fill="#F0C830" opacity="0.85" />
                <path d="M36 24c-4-6-12-8-14-4s4 8 14 4z" fill="#F0C830" stroke="#D8B020" strokeWidth="1" />
                <path d="M36 24c4-6 12-8 14-4s-4 8-14 4z" fill="#F0C830" stroke="#D8B020" strokeWidth="1" />
              </svg>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const TimerBlock: React.FC<{ value: string; unit: string }> = ({ value, unit }) => (
  <div
    className="flex min-w-[44px] flex-col items-center rounded-lg px-2.5 py-1.5"
    style={{
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.12)",
    }}
  >
    <span
      className="font-bold leading-none"
      style={{
        fontFamily: "var(--font-code)",
        fontSize: 18,
        color: "var(--turmeric)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </span>
    <span
      className="mt-0.5 uppercase tracking-[1.2px]"
      style={{ fontSize: "9.5px", color: "rgba(255,255,255,0.5)" }}
    >
      {unit}
    </span>
  </div>
);
